import { useState, type FormEvent } from 'react';
import { api } from '@aeon-karaoke/cloud-convex';
import type { FunctionArgs } from 'convex/server';
import { Button, Input, MonoLabel } from '@/component';
import { cn } from '@/lib/cn';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';

type ShopProduct = FunctionArgs<typeof api.shop.join>['product'];

type LeadState = { kind: 'idle' } | { kind: 'busy' } | { kind: 'joined' } | { kind: 'error'; message: string };

const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

interface ShopLeadFormProps {
  product: ShopProduct;
  email: string;
  /** Given, the form owns the address field; left out, it submits the one above. */
  onEmailChange?: (email: string) => void;
  /** The button, and what replaces the form once the address is on the list. */
  cta: string;
  joinedLabel: string;
  /** Mono note beside the button. */
  note?: string;
  className?: string;
}

/** One "notify me" form on the shop page. Submitting writes the address to
 * the product's list and swaps the form for the confirmation. */
export function ShopLeadForm({ product, email, onEmailChange, cta, joinedLabel, note, className }: ShopLeadFormProps) {
  const [state, setState] = useState<LeadState>({ kind: 'idle' });
  const address = email.trim();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!isEmail(address)) {
      setState({ kind: 'error', message: onEmailChange ? 'Enter an email address.' : 'Enter the address above first.' });
      return;
    }
    setState({ kind: 'busy' });
    const result = await toResult(() => convex.mutation(api.shop.join, { email: address, product }));
    setState(result.ok ? { kind: 'joined' } : { kind: 'error', message: 'Something went wrong. Try again in a minute.' });
  }

  if (state.kind === 'joined') {
    return (
      <div className={cn('flex min-h-[46px] flex-wrap items-center gap-x-4 gap-y-1', className)}>
        <MonoLabel tone="up" size="md">
          {joinedLabel}
        </MonoLabel>
        <span className="text-[15px] text-mute">We will write to {address} first.</span>
        {note && <MonoLabel>{note}</MonoLabel>}
      </div>
    );
  }

  return (
    <form onSubmit={(event) => void submit(event)} noValidate className={className}>
      <div className="flex flex-wrap gap-[10px]">
        {onEmailChange && (
          <Input
            type="email"
            aria-label="Email"
            placeholder="maya@example.com"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            className="h-[46px] w-[290px] max-w-full px-[14px]"
          />
        )}
        <Button type="submit" disabled={state.kind === 'busy'} className={onEmailChange ? 'h-[46px] px-[22px] text-[15px]' : 'px-5 py-[13px]'}>
          {cta}
        </Button>
        {note && <MonoLabel className="self-center">{note}</MonoLabel>}
      </div>
      {state.kind === 'error' && <span className="mt-2 block text-[13px] text-down">{state.message}</span>}
    </form>
  );
}
